import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sparkles, Wind, BookOpen, ChevronRight, Repeat, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import T from "../components/T";
import { slugForPattern, aggregatePatterns } from "../data/patternMap";

const CYCLE_OPT_KEY = "ss_v11_cycle_recognition";
const CYCLE_MIN_COUNT = 3; // gentle threshold — only surface after 3+ occurrences

function isCycleEnabled() {
  try { return window.localStorage.getItem(CYCLE_OPT_KEY) !== "0"; }
  catch { return true; }
}

export default function Decode() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [text, setText] = useState("");
  const [ctx, setCtx] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [kbOpen, setKbOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [openHistory, setOpenHistory] = useState({}); // id → bool (show original)

  const cycleEnabled = isCycleEnabled();

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/decode/history");
      setHistory(Array.isArray(data) ? data : []);
    } catch {
      setHistory((prev) => (Array.isArray(prev) ? prev : []));
    }
  };

  useEffect(() => { loadHistory(); }, []);

  // Library → Decode loop: focus the textarea when ?focus=1
  useEffect(() => {
    if (params.get("focus") === "1") {
      const el = document.querySelector('[data-testid="decode-text-input"]');
      if (el && typeof el.focus === "function") {
        setTimeout(() => el.focus(), 250);
      }
    }
  }, [params]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      const diff = window.innerHeight - vv.height;
      setKbOpen(diff > 120);
    };
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    onResize();
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
    };
  }, []);

  const dismissKb = () => {
    const ae = document.activeElement;
    if (ae && typeof ae.blur === "function") ae.blur();
  };

  const run = async () => {
    if (text.trim().length < 5) {
      toast.info(t("decode.too_short"));
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/decode", {
        text,
        context: ctx || null,
        language: i18n.language || "en",
      });
      setResult(data);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("decode.error"));
    } finally {
      setBusy(false);
    }
  };

  // Recurring pattern aggregation across history (gentle, not diagnostic).
  const recurring = useMemo(
    () => aggregatePatterns(history).filter((p) => p.count >= CYCLE_MIN_COUNT),
    [history]
  );

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-decode">
        <p className="font-script text-2xl text-brand">{t("decode.kicker")}</p>
        <h1 className="font-serif text-4xl leading-tight">
          <T i18nKey="decode.h" />
        </h1>
        <p className="mt-3 text-foreground/70 leading-relaxed">{t("decode.intro")}</p>
      </div>

      <div
        className="px-8 space-y-3 pb-6"
        onClick={(e) => { if (e.target === e.currentTarget) dismissKb(); }}
      >
        <Input
          data-testid="decode-context-input"
          placeholder={t("decode.context_placeholder")}
          value={ctx}
          onChange={(e) => setCtx(e.target.value)}
          enterKeyHint="next"
          className="h-12 rounded-2xl bg-card border-border px-5"
        />
        <Textarea
          data-testid="decode-text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("decode.text_placeholder")}
          enterKeyHint="done"
          className="min-h-44 rounded-2xl bg-card border-border px-5 py-4 text-[15px] leading-relaxed resize-none"
        />
        <Button
          data-testid="decode-submit-btn"
          onClick={() => { dismissKb(); run(); }}
          disabled={busy}
          className="w-full h-14 rounded-full bg-brand hover:bg-brand/90 text-white shadow-md"
        >
          {busy ? t("decode.submitting") : t("decode.submit")}
          <Sparkles className="w-4 h-4 ml-2" />
        </Button>
        <p className="text-xs text-foreground/55 leading-relaxed pt-1">{t("decode.disclaimer")}</p>
      </div>

      {kbOpen && (
        <button
          type="button"
          data-testid="decode-dismiss-kb"
          onMouseDown={(e) => e.preventDefault()}
          onClick={dismissKb}
          className="fixed right-4 z-40 bg-brand text-white text-xs px-4 py-2 rounded-full shadow-lg active:scale-95 transition-transform"
          style={{
            bottom: `calc(${
              window.visualViewport
                ? Math.max(window.innerHeight - window.visualViewport.height + 12, 12)
                : 12
            }px)`,
          }}
        >
          {t("common.done")}
        </button>
      )}

      {result && (
        <div className="px-8 mt-6 space-y-4 pb-10 animate-fade-up" data-testid="decode-result">
          <section className="rounded-3xl bg-card border border-border p-6">
            <p className="text-xs uppercase tracking-widest text-foreground/55">{t("decode.result.gentle_read")}</p>
            <p className="mt-2 font-serif text-xl leading-snug">{result.summary || ""}</p>
          </section>

          <section className="rounded-3xl bg-card border border-border p-6">
            <p className="text-xs uppercase tracking-widest text-foreground/55">{t("decode.result.tone")}</p>
            <p className="mt-2 text-foreground/85 leading-relaxed">{result.tone_reading || ""}</p>
          </section>

          {Array.isArray(result.possible_patterns) && result.possible_patterns.length > 0 && (
            <section className="rounded-3xl bg-card border border-border p-6">
              <p className="text-xs uppercase tracking-widest text-foreground/55">
                {t("decode.result.patterns")}
              </p>
              <div className="mt-3 space-y-3">
                {result.possible_patterns.map((p, i) => {
                  const slug = slugForPattern(p?.name || "");
                  return (
                    <div key={i} className="rounded-2xl bg-brand/5 border border-brand/15 p-4">
                      <div className="font-serif text-lg text-foreground">{p?.name || ""}</div>
                      <p className="text-sm mt-1 text-foreground/80">{p?.description || ""}</p>
                      {p?.why_confusing && (
                        <p className="text-sm mt-2 text-foreground/65 italic">
                          {t("decode.result.why_disorient")} {p.why_confusing}
                        </p>
                      )}
                      {slug && (
                        <button
                          data-testid={`decode-learn-more-${slug}`}
                          onClick={() => navigate(`/library/${slug}`)}
                          className="mt-3 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                        >
                          <BookOpen className="w-3 h-3" />
                          {t("decode.learn_more")} <ChevronRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="rounded-3xl bg-brand/8 border border-brand/20 p-6">
            <p className="text-xs uppercase tracking-widest text-brand-deep flex items-center gap-2">
              <Wind className="w-3 h-3" /> {t("decode.result.grounding")}
            </p>
            <ul className="mt-3 space-y-2">
              {(Array.isArray(result.grounding_prompts) ? result.grounding_prompts : []).map((g, i) => (
                <li key={i} className="text-foreground/85 leading-relaxed">· {g}</li>
              ))}
            </ul>
            {result.gentle_next_step && (
              <p className="mt-4 font-serif italic text-foreground/90">
                {result.gentle_next_step}
              </p>
            )}
          </section>
        </div>
      )}

      {/* Recurring pattern recognition — opt-in, gentle, never diagnostic. */}
      {cycleEnabled && recurring.length > 0 && (
        <div className="px-8 mt-2 mb-2" data-testid="decode-recurring">
          <section className="rounded-3xl bg-brand/8 border border-brand/20 p-5">
            <p className="text-xs uppercase tracking-widest text-brand-deep flex items-center gap-2">
              <Repeat className="w-3 h-3" /> {t("decode.recurring.title")}
            </p>
            <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
              {t("decode.recurring.intro")}
            </p>
            <div className="mt-3 space-y-2">
              {recurring.slice(0, 3).map((r) => (
                <button
                  key={r.slug}
                  data-testid={`recurring-${r.slug}`}
                  onClick={() => navigate(`/library/${r.slug}`)}
                  className="w-full flex items-center justify-between gap-2 text-left rounded-2xl bg-card/70 border border-border p-3 hover:border-brand/40 transition-all"
                >
                  <span className="text-sm text-foreground/85">
                    {t("decode.recurring.line", { name: t(`pattern.${r.slug}`, { defaultValue: r.slug }), n: r.count })}
                  </span>
                  <ChevronRight className="w-4 h-4 text-foreground/40 shrink-0" />
                </button>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-foreground/50 leading-relaxed italic">
              {t("decode.recurring.footer")}
            </p>
          </section>
        </div>
      )}

      {Array.isArray(history) && history.length > 0 && (
        <div className="px-8 mt-2 pb-10" data-testid="decode-history">
          <p className="text-xs uppercase tracking-widest text-foreground/55">
            {t("decode.history.title")}
          </p>
          <div className="mt-3 space-y-2">
            {history.map((h) => {
              const isOpen = !!openHistory[h.id];
              return (
                <article
                  key={h.id}
                  data-testid={`decode-history-${h.id}`}
                  className="rounded-2xl bg-card border border-border p-4"
                >
                  <div className="text-[11px] text-foreground/45">
                    {h.created_at ? new Date(h.created_at).toLocaleString() : ""}
                  </div>
                  {h.summary && (
                    <p className="mt-1 font-serif italic text-foreground/85 leading-snug">
                      {h.summary}
                    </p>
                  )}
                  {Array.isArray(h.pattern_names) && h.pattern_names.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {h.pattern_names.map((p, pi) => {
                        const slug = slugForPattern(p);
                        if (slug) {
                          return (
                            <button
                              key={pi}
                              data-testid={`history-pattern-${slug}`}
                              onClick={() => navigate(`/library/${slug}`)}
                              className="text-[11px] px-2 py-0.5 rounded-full bg-brand/10 text-brand hover:bg-brand/20 transition-all"
                            >
                              {p}
                            </button>
                          );
                        }
                        return (
                          <span
                            key={pi}
                            className="text-[11px] px-2 py-0.5 rounded-full bg-brand/10 text-brand"
                          >
                            {p}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {h.input_text && (
                    <div className="mt-3">
                      <button
                        data-testid={`history-toggle-${h.id}`}
                        onClick={() => setOpenHistory((o) => ({ ...o, [h.id]: !isOpen }))}
                        className="inline-flex items-center gap-1 text-[11px] text-foreground/55 hover:text-brand"
                      >
                        {isOpen
                          ? <>{t("decode.history.hide_original")} <ChevronUp className="w-3 h-3" /></>
                          : <>{t("decode.history.show_original")} <ChevronDown className="w-3 h-3" /></>}
                      </button>
                      {isOpen && (
                        <div className="mt-2 rounded-xl bg-background/60 border border-border p-3">
                          {h.context && (
                            <p className="text-[11px] uppercase tracking-widest text-foreground/45 mb-1">
                              {t("decode.history.context")}
                            </p>
                          )}
                          {h.context && (
                            <p className="text-sm text-foreground/70 italic mb-2">{h.context}</p>
                          )}
                          <p className="text-[11px] uppercase tracking-widest text-foreground/45 mb-1">
                            {t("decode.history.original")}
                          </p>
                          <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">
                            {h.input_text}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      )}
    </AppShell>
  );
}
